import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, AppBar, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import AdminGlobalSettings from '../../components/Admin/AdminGlobalSettings';
import AdminUserManagement from '../../components/AdminUserManagement';
import AdminExistingQuestions from '../../components/Admin/AdminExistingQuestions';
import FirmManagementPage from '../../components/Admin/FirmManagementPage';
import stripeConfig from '../../config/stripe';
import { functions } from '../../firebase';
import { toast } from 'react-toastify';
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`admin-tabpanel-${index}`}
            aria-labelledby={`admin-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const AdminDashboard = () => {
    const { user, logout } = useAuth();
    const [currentTab, setCurrentTab] = useState(0);
    const [clients, setClients] = useState([]);

    // Fetch all clients for the admin view
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const clientsSnapshot = await getDocs(collection(db, 'clients'));
                const clientsList = clientsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setClients(clientsList);
                console.log('[ADMIN] Loaded clients:', clientsList.length);
            } catch (error) {
                console.error('[ADMIN] Error fetching clients:', error);
            }
        };

        fetchClients();
    }, []);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f4f6f8' }}>
            <AppBar position="static" color="default" elevation={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: 'white' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                        SavvaiEdge Admin
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body1">{user?.email}</Typography>
                        <Button variant="contained" color="primary" onClick={logout}>
                            Logout
                        </Button>
                    </Box>
                </Box>
            </AppBar>
            <Box sx={{ flexGrow: 1, p: 3 }}>
                <Paper elevation={2}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={currentTab} onChange={handleTabChange} aria-label="admin portal tabs">
                            <Tab label="Dashboard" id="admin-tab-0" />
                            <Tab label="User & Client Management" id="admin-tab-1" />
                            <Tab label="Firm Portal" id="admin-tab-2" />
                            <Tab label="Global Settings" id="admin-tab-3" />
                        </Tabs>
                    </Box>
                    <TabPanel value={currentTab} index={0}>
                        <AdminExistingQuestions 
                            clientId="ALL" 
                            user={user}
                            isAdmin={true} 
                            clients={clients}
                            clientIsActive={true}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={1}>
                        <AdminUserManagement />
                    </TabPanel>
                    <TabPanel value={currentTab} index={2}>
                        <FirmManagementPage />
                    </TabPanel>
                    <TabPanel value={currentTab} index={3}>
                        <AdminGlobalSettings />
                    </TabPanel>

                    {/* Admin Claims Sync - Add this near the Stripe test section */}
<Paper sx={{ p: 3, mt: 3, border: '2px dashed orange' }}>
    <Typography variant="h6" color="warning.main" gutterBottom>
        🔧 Sync Super-Admin Claims (One-Time Fix)
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
        This will set Firebase Auth custom claims for all users with admin: true in Firestore.
        Required for Firestore security rules to work correctly on list queries.
    </Typography>
    <Button
        variant="contained"
        color="warning"
        onClick={async () => {
            try {
                const { httpsCallable } = await import('firebase/functions');
                const syncAdminClaims = httpsCallable(functions, 'adminSyncAllAdminClaims');
                const result = await syncAdminClaims({});
                console.log('Sync result:', result.data);
                toast.success(result.data.message);
            } catch (err) {
                console.error('Sync failed:', err);
                toast.error(`Failed: ${err.message}`);
            }
        }}
    >
        SYNC ADMIN CLAIMS
    </Button>
</Paper>

                     {/* TEMPORARY: Stripe Test Section - Remove after testing */}
                    <Box sx={{ p: 3, mt: 2, border: '2px dashed orange', borderRadius: 1, backgroundColor: '#fff3e0' }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            🧪 Stripe Integration Test (Remove After Testing)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            This will create a Stripe checkout session with 14-day trial for the currently logged-in user.
                        </Typography>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={async () => {
                                try {
                                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                                    const functions = getFunctions();
                                    const createCheckout = httpsCallable(functions, 'createCheckoutSession');
                                    
                                    console.log('🧪 Creating checkout session...');
                                    const result = await createCheckout({ 
    idempotencyKey: `test-${Date.now()}` 
});
console.log('✅ Checkout session created:', result.data.sessionId);
alert('Checkout session created! Redirecting to Stripe...');

// Load Stripe and redirect properly
const { loadStripe } = await import('@stripe/stripe-js');
const stripe = await loadStripe(stripeConfig.publishableKey);
const { error } = await stripe.redirectToCheckout({ sessionId: result.data.sessionId });
if (error) {
    alert(`Redirect error: ${error.message}`);
}
                                    
                                } catch (error) {
                                    console.error('❌ Error:', error);
                                    alert(`Error creating checkout: ${error.message}`);
                                }
                            }}
                        >
                            🧪 Test Stripe Checkout (14-Day Trial)
                        </Button>
                    </Box>

                    {/* TEMPORARY: Theme Canonicalization Backfill - Remove after running */}
                    <Box sx={{ p: 3, mt: 2, border: '2px dashed #4caf50', borderRadius: 1, backgroundColor: '#e8f5e9' }}>
                        <Typography variant="h6" color="success.main" gutterBottom>
                            🔧 Theme Canonicalization Backfill (One-Time)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Populates canonicalThemes on historical documents for accurate Industry Insights aggregation.
                            Run Dry Run first to preview, then Run Backfill to apply changes.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={async () => {
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const backfillFn = httpsCallable(functions, 'backfillCanonicalThemes');
                                        
                                        console.log('🔍 Running dry run...');
                                        const result = await backfillFn({ 
                                            dryRun: true, 
                                            batchSize: 500, 
                                            collection: 'all' 
                                        });
                                        console.log('✅ Dry run complete:', result.data);
                                        alert(`DRY RUN COMPLETE:\n\nQuestions: ${result.data.results.questions.updated} would be updated\nSignals: ${result.data.results.signals.updated} would be updated\nMemos: ${result.data.results.decisionMemos.updated} would be updated\n\nTotal: ${result.data.results.totalUpdated} documents\n\nCheck console for full details.`);
                                        
                                    } catch (error) {
                                        console.error('❌ Dry run error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🔍 Dry Run (Preview)
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={async () => {
                                    if (!window.confirm('This will update historical documents with canonicalThemes. Continue?')) {
                                        return;
                                    }
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const backfillFn = httpsCallable(functions, 'backfillCanonicalThemes');
                                        
                                        console.log('🚀 Running backfill...');
                                        const result = await backfillFn({ 
                                            dryRun: false, 
                                            batchSize: 500, 
                                            collection: 'all' 
                                        });
                                        console.log('✅ Backfill complete:', result.data);
                                        alert(`BACKFILL COMPLETE:\n\nQuestions updated: ${result.data.results.questions.updated}\nSignals updated: ${result.data.results.signals.updated}\nMemos updated: ${result.data.results.decisionMemos.updated}\n\nTotal: ${result.data.results.totalUpdated} documents updated\n\nCheck console for full details.`);
                                        
                                    } catch (error) {
                                        console.error('❌ Backfill error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🚀 Run Backfill
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Note: If you have more than 500 documents, run multiple times until "0 updated" is reported.
                        </Typography>
                    </Box>

                    {/* TEMPORARY: AI Governance Backfill - Remove after running in all environments */}
                    <Box sx={{ p: 3, mt: 2, border: '2px dashed #1976d2', borderRadius: 1, backgroundColor: '#e3f2fd' }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                            🔒 AI Governance Backfill (One-Time)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Adds <code>provider</code> and <code>aiAuditMetadata</code> fields to historical Q&A, SavvaiSignals, and Decision Memos for governance compliance.
                            Run Dry Run first to preview, then Run Backfill to apply changes.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={async () => {
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const backfillFn = httpsCallable(functions, 'backfillProviderField');
                                        
                                        console.log('🔍 Running AI Governance dry run...');
                                        const result = await backfillFn({ dryRun: true, batchSize: 500 });
                                        console.log('✅ Dry run complete:', result.data);
                                        alert(`DRY RUN COMPLETE:\n\nQuestions: ${result.data.results.questions.updated} would be updated (${result.data.results.questions.skipped} already compliant)\nSavvaiSignals: ${result.data.results.businessUpdates.updated} would be updated (${result.data.results.businessUpdates.skipped} already compliant)\nDecision Memos: ${result.data.results.decisionMemos.updated} would be updated (${result.data.results.decisionMemos.skipped} already compliant)\n\nTotal: ${result.data.totalUpdated} documents\n\nCheck console for full details.`);
                                        
                                    } catch (error) {
                                        console.error('❌ Dry run error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🔍 Dry Run (Preview)
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={async () => {
                                    if (!window.confirm('This will add provider and aiAuditMetadata to historical documents. Continue?')) {
                                        return;
                                    }
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const backfillFn = httpsCallable(functions, 'backfillProviderField');
                                        
                                        console.log('🚀 Running AI Governance backfill...');
                                        const result = await backfillFn({ dryRun: false, batchSize: 500 });
                                        console.log('✅ Backfill complete:', result.data);
                                        alert(`BACKFILL COMPLETE:\n\nQuestions updated: ${result.data.results.questions.updated}\nSavvaiSignals updated: ${result.data.results.businessUpdates.updated}\nDecision Memos updated: ${result.data.results.decisionMemos.updated}\n\nTotal: ${result.data.totalUpdated} documents updated\n\nCheck console for full details.`);
                                        
                                    } catch (error) {
                                        console.error('❌ Backfill error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🚀 Run Backfill
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Note: Run in each environment (Dev → CC → Prod). If you have more than 500 documents, run multiple times until "0 updated" is reported.
                        </Typography>
                    </Box>
                    {/* TEMPORARY: Anonymization Metadata Backfill - Remove after running in all environments */}
<Box sx={{ p: 3, mt: 2, border: '2px dashed #9c27b0', borderRadius: 1, backgroundColor: '#f3e5f5' }}>
    <Typography variant="h6" color="secondary" gutterBottom>
        🛡️ Anonymization Metadata Backfill (Phase 2)
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
        Adds <code>anonymizationApplied</code>, <code>anonymizationTimestamp</code>, and <code>anonymizationDetails</code> 
        to historical Q&A, SavvaiSignals, and Decision Memos for SOC 2 data protection compliance.
        Documents created before Phase 1 deployment will be marked with <code>backfilled: true</code>.
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
            variant="contained"
            color="secondary"
            onClick={async () => {
                if (!window.confirm('This will add anonymization metadata to all historical documents missing it. Continue?')) {
                    return;
                }
                try {
                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                    const functions = getFunctions();
                    const backfillFn = httpsCallable(functions, 'backfillAnonymizationMetadata');
                    
                    console.log('🛡️ Running Anonymization Metadata backfill...');
                    const result = await backfillFn({});
                    console.log('✅ Backfill complete:', result.data);
                    alert(`BACKFILL COMPLETE:\n\nQuestions updated: ${result.data.totals.questionsUpdated} (${result.data.totals.questionsSkipped} already had metadata)\nSignals updated: ${result.data.totals.signalsUpdated} (${result.data.totals.signalsSkipped} already had metadata)\nDecision Memos updated: ${result.data.totals.memosUpdated} (${result.data.totals.memosSkipped} already had metadata)\n\nTotal: ${result.data.totals.totalUpdated} documents updated\n\n${result.data.errors ? 'Errors: ' + result.data.errors.length : 'No errors'}\n\nCheck console for full details.`);
                    
                } catch (error) {
                    console.error('❌ Backfill error:', error);
                    alert(`Error: ${error.message}`);
                }
            }}
        >
            🛡️ Run Anonymization Backfill
        </Button>
    </Box>
    <Typography variant="caption" color="text.secondary">
        Note: Run in each environment (Dev → Prod). If you have more than 500 documents per collection, run multiple times until "0 updated" is reported. Safe to run multiple times - already-updated documents are skipped.
    </Typography>
</Box>

{/* TEMPORARY: reportType Backfill — Phase 3B — Remove after running in all environments */}
<Box sx={{ p: 3, mt: 2, border: '2px dashed #ff9800', borderRadius: 1, backgroundColor: '#fff8e1' }}>
    <Typography variant="h6" sx={{ color: '#e65100' }} gutterBottom>
        📋 Signal reportType Backfill — Phase 3B (One-Time)
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
        Adds <code>reportType</code> to existing <code>businessUpdates</code> documents that are missing it.
        Sets <code>"welcome"</code> for first signals and <code>"digest"</code> for all others.
        Run Dry Run first, then Run Backfill. If more documents remain, continue using the returned cursor until complete.
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
            variant="outlined"
            sx={{ color: '#e65100', borderColor: '#e65100' }}
            onClick={async () => {
                try {
                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                    const functions = getFunctions();
                    const backfillFn = httpsCallable(functions, 'backfillReportType');

                    console.log('🔍 Running reportType dry run...');
                    const result = await backfillFn({
                        dryRun: true,
                        batchSize: 500
                    });

                    console.log('✅ Dry run complete:', result.data);

                    alert(
                        `DRY RUN COMPLETE:\n\n` +
                        `Would update: ${result.data.updated} documents\n` +
                        `  → welcome: ${result.data.welcome}\n` +
                        `  → digest: ${result.data.digest}\n` +
                        `Already set (skipped): ${result.data.skipped}\n` +
                        `Errors: ${result.data.errors}\n` +
                        `Has more: ${result.data.hasMore ? 'YES' : 'NO'}\n` +
                        `${result.data.nextCursor ? '\nNext cursor returned in console.' : ''}\n\n` +
                        `Check console for full details.`
                    );
                } catch (error) {
                    console.error('❌ Dry run error:', error);
                    alert(`Error: ${error.message}`);
                }
            }}
        >
            🔍 Dry Run (Preview)
        </Button>

        <Button
            variant="contained"
            sx={{ backgroundColor: '#e65100' }}
            onClick={async () => {
                if (!window.confirm(
                    'This will add reportType to businessUpdates documents that are missing it.\n\n' +
                    'Run Dry Run first to confirm counts.\n\nContinue?'
                )) {
                    return;
                }

                try {
                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                    const functions = getFunctions();
                    const backfillFn = httpsCallable(functions, 'backfillReportType');

                    let totalUpdated = 0;
                    let totalWelcome = 0;
                    let totalDigest = 0;
                    let totalSkipped = 0;
                    let totalErrors = 0;
                    let nextCursor = null;
                    let pass = 1;
                    let hasMore = true;

                    while (hasMore) {
                        console.log(`🚀 Running reportType backfill pass ${pass}...`, nextCursor);

                        const payload = {
                            dryRun: false,
                            batchSize: 500,
                            ...(nextCursor || {})
                        };

                        const result = await backfillFn(payload);
                        const data = result.data;

                        console.log(`✅ Backfill pass ${pass} complete:`, data);

                        totalUpdated += data.updated || 0;
                        totalWelcome += data.welcome || 0;
                        totalDigest += data.digest || 0;
                        totalSkipped += data.skipped || 0;
                        totalErrors += data.errors || 0;

                        hasMore = !!data.hasMore;
                        nextCursor = data.nextCursor || null;
                        pass += 1;

                        if (!hasMore) {
                            break;
                        }
                    }

                    alert(
                        `BACKFILL COMPLETE:\n\n` +
                        `Updated: ${totalUpdated} documents\n` +
                        `  → welcome: ${totalWelcome}\n` +
                        `  → digest: ${totalDigest}\n` +
                        `Skipped (already set): ${totalSkipped}\n` +
                        `Errors: ${totalErrors}\n\n` +
                        `All pages processed. Check console for full details.`
                    );
                } catch (error) {
                    console.error('❌ Backfill error:', error);
                    alert(`Error: ${error.message}`);
                }
            }}
        >
            🚀 Run Backfill
        </Button>
    </Box>
    <Typography variant="caption" color="text.secondary">
        Note: Processes 500 documents per pass with cursor-based pagination. Run in Dev first, then Prod after verification.
    </Typography>
</Box>

{/* TEMPORARY: Governance Phase B Review Testing - Remove after testing */}
                    <Box sx={{ p: 3, mt: 2, border: '2px dashed #2e7d32', borderRadius: 1, backgroundColor: '#e8f5e9' }}>
                        <Typography variant="h6" color="success.main" gutterBottom>
                            🔍 Governance Phase B — Periodic Review Testing
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Tests for the periodic review sampling system. Run in order: Test 1 first (submit a Q&A with a real name),
                            then Tests 2-6 to verify the review pipeline.
                        </Typography>

                        {/* Test 1: Check sanitizedText in recent governance event */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f1f8e9', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Test 1: Verify sanitizedText Field</Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                Fetches the most recent governance event and checks if the new sanitizedText field exists (vs just the 80-char textPreview).
                            </Typography>
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={async () => {
                                    try {
                                        const { collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
                                        const eventsQuery = query(
                                            collection(db, 'governanceEvents'),
                                            orderBy('timestamp', 'desc'),
                                            limit(1)
                                        );
                                        const snap = await getDocs(eventsQuery);
                                        if (snap.empty) {
                                            alert('No governance events found. Submit a Q&A question first.');
                                            return;
                                        }
                                        const doc = snap.docs[0];
                                        const data = doc.data();
                                        const hasSanitizedText = !!data.sanitizedText;
                                        const schemaVersion = data.schemaVersion || 'none';
                                        const preview = data.textPreview || '[none]';
                                        const fullLength = data.sanitizedText ? data.sanitizedText.length : 0;

                                        console.log('Governance event:', doc.id, data);
                                        alert(
                                            `GOVERNANCE EVENT: ${doc.id}\n\n` +
                                            `schemaVersion: ${schemaVersion}\n` +
                                            `textPreview (${preview.length} chars): ${preview}\n` +
                                            `sanitizedText present: ${hasSanitizedText ? '✅ YES' : '❌ NO'}\n` +
                                            `sanitizedText length: ${fullLength} chars\n` +
                                            `riskLevel: ${data.riskLevel}\n` +
                                            `dataPath: ${data.dataPath}\n\n` +
                                            (hasSanitizedText ? '✅ Phase B Item #1 PASSED' : '❌ Phase B Item #1 FAILED — deploy governanceEnforcement.js changes first')
                                        );
                                    } catch (error) {
                                        console.error('Test 1 error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🔍 Check Latest Governance Event
                            </Button>
                        </Box>

                        {/* Test 2: Get Review Sample — Previous Month (likely zero events) */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f1f8e9', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Test 2: Get Review Sample (Previous Month)</Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                Calls getGovernanceReviewSample for January 2026. Likely returns zeroEvents: true unless you had HIGH/CRITICAL events.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={async () => {
                                    try {
                                        const { httpsCallable } = await import('firebase/functions');
                                        const fn = httpsCallable(functions, 'getGovernanceReviewSample');
                                        console.log('Calling getGovernanceReviewSample for Jan 2026...');
                                        const result = await fn({ periodYear: 2026, periodMonth: 0 });
                                        console.log('Sample result:', result.data);
                                        const d = result.data;
                                        alert(
                                            `REVIEW SAMPLE — January 2026\n\n` +
                                            `zeroEvents: ${d.zeroEvents ? '✅ YES' : '❌ NO'}\n` +
                                            `alreadyReviewed: ${d.alreadyReviewed || false}\n` +
                                            `totalEventsInPeriod: ${d.totalEventsInPeriod}\n` +
                                            `highCriticalCount: ${d.highCriticalCount}\n` +
                                            `sampleSize: ${d.sampleSize}\n` +
                                            `riskBreakdown: ${JSON.stringify(d.riskBreakdown)}\n` +
                                            `sampledEvents: ${d.sampledEvents?.length || 0} items\n\n` +
                                            `See console for full details.`
                                        );
                                    } catch (error) {
                                        console.error('Test 2 error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🔍 Get Jan 2026 Sample
                            </Button>
                        </Box>

                        {/* Test 3: Auto-Submit Zero Events Review */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f1f8e9', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Test 3: Auto-Submit Zero Events Review (January 2026)</Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                Creates an auto-generated clean review log for January 2026. Run after Test 2 confirms zeroEvents: true.
                                Running again should return alreadyExists: true.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={async () => {
                                    try {
                                        const { httpsCallable } = await import('firebase/functions');
                                        const fn = httpsCallable(functions, 'autoSubmitZeroEventsReview');
                                        console.log('Calling autoSubmitZeroEventsReview for Jan 2026...');
                                        const result = await fn({ periodYear: 2026, periodMonth: 0 });
                                        console.log('Zero events result:', result.data);
                                        const d = result.data;
                                        alert(
                                            `ZERO EVENTS REVIEW — January 2026\n\n` +
                                            `success: ${d.success}\n` +
                                            `reviewLogId: ${d.reviewLogId}\n` +
                                            `autoGenerated: ${d.autoGenerated || false}\n` +
                                            `alreadyExists: ${d.alreadyExists || false}\n\n` +
                                            `Check governanceReviewLogs in Firestore for the new document.`
                                        );
                                    } catch (error) {
                                        console.error('Test 3 error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                📝 Auto-Submit Zero Events (Jan)
                            </Button>
                        </Box>

                        {/* Test 4: Get Review Sample — Current Month (should have events from today's testing) */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f1f8e9', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Test 4: Get Review Sample (February 2026 — Current Month)</Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                Calls getGovernanceReviewSample for February 2026. If you only have LOW/MEDIUM events, this will also return zeroEvents: true.
                                To test the sampling path, temporarily change one event's riskLevel to HIGH in Firestore, run this, then revert.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={async () => {
                                    try {
                                        const { httpsCallable } = await import('firebase/functions');
                                        const fn = httpsCallable(functions, 'getGovernanceReviewSample');
                                        console.log('Calling getGovernanceReviewSample for Feb 2026...');
                                        const result = await fn({ periodYear: 2026, periodMonth: 1 });
                                        console.log('Sample result:', result.data);
                                        const d = result.data;
                                        let msg = `REVIEW SAMPLE — February 2026\n\n` +
                                            `zeroEvents: ${d.zeroEvents ? '✅ YES' : '❌ NO'}\n` +
                                            `alreadyReviewed: ${d.alreadyReviewed || false}\n` +
                                            `totalEventsInPeriod: ${d.totalEventsInPeriod}\n` +
                                            `highCriticalCount: ${d.highCriticalCount}\n` +
                                            `sampleSize: ${d.sampleSize}\n` +
                                            `riskBreakdown: ${JSON.stringify(d.riskBreakdown)}\n`;

                                        if (d.sampledEvents && d.sampledEvents.length > 0) {
                                            msg += `\n--- SAMPLED EVENTS ---\n`;
                                            d.sampledEvents.forEach((e, i) => {
                                                msg += `\n[${i+1}] ${e.clientName} — ${e.dataPath}\n` +
                                                    `    Risk: ${e.riskLevel}, PII: ${e.totalPiiFound}\n` +
                                                    `    Text (first 100): ${(e.sanitizedText || '').substring(0, 100)}...\n`;
                                            });
                                        }
                                        msg += `\nSee console for full details.`;
                                        alert(msg);
                                    } catch (error) {
                                        console.error('Test 4 error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🔍 Get Feb 2026 Sample
                            </Button>
                        </Box>

                        {/* Test 5: Submit Clean Review */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f1f8e9', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Test 5: Submit Clean Review (February 2026)</Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                Submits a review log with controls_effective. First run Test 4 and note an eventId from the console.
                                Uses a hardcoded test eventId — replace in console if needed. Will fail if Feb already reviewed.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={async () => {
                                    try {
                                        // First get the sample to find real eventIds
                                        const { httpsCallable } = await import('firebase/functions');
                                        const sampleFn = httpsCallable(functions, 'getGovernanceReviewSample');
                                        const sampleResult = await sampleFn({ periodYear: 2026, periodMonth: 1 });
                                        const sd = sampleResult.data;

                                        if (sd.alreadyReviewed) {
                                            alert(`February 2026 already reviewed.\nReview Log ID: ${sd.reviewLogId}`);
                                            return;
                                        }

                                        const eventIds = (sd.sampledEvents || []).map(e => e.eventId);
                                        if (eventIds.length === 0) {
                                            alert('No sampled events to review. If zeroEvents, use Test 3 instead.');
                                            return;
                                        }

                                        const submitFn = httpsCallable(functions, 'submitGovernanceReviewLog');
                                        console.log('Submitting clean review with eventIds:', eventIds);
                                        const result = await submitFn({
                                            periodYear: 2026,
                                            periodMonth: 1,
                                            sampledEventIds: eventIds,
                                            itemsConfirmedClean: eventIds.length,
                                            findingsCount: 0,
                                            findingsDetails: null,
                                            overallAssessment: 'controls_effective',
                                        });
                                        console.log('Submit result:', result.data);
                                        alert(
                                            `REVIEW SUBMITTED — February 2026\n\n` +
                                            `success: ${result.data.success}\n` +
                                            `reviewLogId: ${result.data.reviewLogId}\n` +
                                            `overallAssessment: ${result.data.overallAssessment}\n` +
                                            `incidentId: ${result.data.incidentId || 'none'}\n\n` +
                                            `Check governanceReviewLogs in Firestore.`
                                        );
                                    } catch (error) {
                                        console.error('Test 5 error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                ✅ Submit Clean Review (Feb)
                            </Button>
                        </Box>

                        {/* Test 6: Submit Finding Review (creates incident + sends email) */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Test 6: Submit Finding Review (Creates Incident + Admin Email)</Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 1, color: 'warning.main' }}>
                                ⚠️ This creates a real governanceIncidents record and sends an admin notification email.
                                Only run this to test the incident pipeline. Will fail if the month is already reviewed — 
                                delete the existing Feb review log from Firestore first if re-testing.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                onClick={async () => {
                                    if (!window.confirm('This will create a governance INCIDENT and send an admin email. Continue?')) {
                                        return;
                                    }
                                    try {
                                        const { httpsCallable } = await import('firebase/functions');
                                        const sampleFn = httpsCallable(functions, 'getGovernanceReviewSample');
                                        const sampleResult = await sampleFn({ periodYear: 2026, periodMonth: 1 });
                                        const sd = sampleResult.data;

                                        if (sd.alreadyReviewed) {
                                            alert(`February 2026 already reviewed.\nDelete the review log from Firestore to re-test.\nReview Log ID: ${sd.reviewLogId}`);
                                            return;
                                        }

                                        const eventIds = (sd.sampledEvents || []).map(e => e.eventId);
                                        if (eventIds.length === 0) {
                                            alert('No sampled events. Need HIGH/CRITICAL events to test findings.');
                                            return;
                                        }

                                        const submitFn = httpsCallable(functions, 'submitGovernanceReviewLog');
                                        console.log('Submitting FINDING review with eventIds:', eventIds);
                                        const result = await submitFn({
                                            periodYear: 2026,
                                            periodMonth: 1,
                                            sampledEventIds: eventIds,
                                            itemsConfirmedClean: Math.max(0, eventIds.length - 1),
                                            findingsCount: 1,
                                            findingsDetails: 'TEST FINDING: Simulated PII leak detected during Phase B testing. Not a real incident.',
                                            overallAssessment: 'finding_identified',
                                            findingEventIds: [eventIds[0]],
                                        });
                                        console.log('Finding result:', result.data);
                                        alert(
                                            `⚠️ FINDING SUBMITTED — February 2026\n\n` +
                                            `success: ${result.data.success}\n` +
                                            `reviewLogId: ${result.data.reviewLogId}\n` +
                                            `overallAssessment: ${result.data.overallAssessment}\n` +
                                            `incidentId: ${result.data.incidentId}\n\n` +
                                            `Check:\n` +
                                            `1. governanceReviewLogs in Firestore\n` +
                                            `2. governanceIncidents in Firestore\n` +
                                            `3. Admin email inbox for notification`
                                        );
                                    } catch (error) {
                                        console.error('Test 6 error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                ⚠️ Submit Finding (Feb — Creates Incident)
                            </Button>
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                            Test order: 1 → submit a Q&A with real name → 1 again → 2 → 3 → manually set one event to HIGH in Firestore → 4 → 5 or 6 → revert the HIGH event.
                        </Typography>
                    </Box>

                </Paper>
            </Box>
        </Box>
    );
};

export default AdminDashboard;